import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getDevices } from '../../utils/functions';
import {
  Device,
  DEVICE_KIND,
  IRecordConfigSettings,
  IRecordSettingsProvider,
  IUserSettings,
  Option,
  VIDEO_QUALITY,
} from './types';

export const RecordSettingsContext = createContext(
  {} as {
    recorderSettings: { audioSource: Option[]; videoSource: Option[] };
    setUserSettings: React.Dispatch<React.SetStateAction<IUserSettings>>;
    userSettings: IUserSettings;
    recordingError: string | null;
    updateErrorMessage: () => void;
    initialUserSettings: IUserSettings;
    onSubmitModalHandler: () => void;
  }
);

const LOCAL_STORAGE_KEY = 'record_settings';

const initialSettings: IUserSettings = {
  audioSource: null,
  videoSource: null,
  videoQuality: VIDEO_QUALITY.STANDARD,
};

const getUserSettings = (
  defaultAudio: Option,
  defaultVideo: Option
): IUserSettings => {
  try {
    const item = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (item) return JSON.parse(item) as IUserSettings;

    return {
      videoQuality: VIDEO_QUALITY.STANDARD,
      videoSource: defaultVideo,
      audioSource: defaultAudio,
    };
  } catch (error) {
    console.error('Error parsing user settings from localStorage:', error);
    return {
      videoQuality: VIDEO_QUALITY.STANDARD,
      videoSource: defaultVideo,
      audioSource: defaultAudio,
    };
  }
};

export const RecordSettingsProvider = ({
  children,
}: IRecordSettingsProvider) => {
  const { t, ready, i18n: i18nFromHook } = useTranslation();

  // Recompute these whenever language changes (critical)
  const noAudio: Option = useMemo(
    () => ({ label: t('No Audio'), value: false }),
    [t, i18nFromHook.resolvedLanguage]
  );
  const noVideo: Option = useMemo(
    () => ({ label: t('No Video'), value: false }),
    [t, i18nFromHook.resolvedLanguage]
  );

  const [recorderSettings, setRecorderSettings] =
    useState<IRecordConfigSettings>({
      audioSource: [],
      videoSource: [],
    });

  const [userSettings, setUserSettings] =
    useState<IUserSettings>(initialSettings);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [initialUserSettingsState, setInitialUserSettingsState] =
    useState<IUserSettings>(initialSettings);

  const updateErrorMessage = () => {
    setRecordingError(
      t(
        'You do not have the necessary permissions to access this feature. Please check your browser permissions.'
      )
    );
  };

  const fetchDevices = async () => {
    try {
      const devicesResponse = await getDevices();
      const devices: Device[] = devicesResponse as Device[];

      const newConfig = devices.reduce(
        (config, device) => {
          if (device.kind === DEVICE_KIND.AUDIO_INPUT) {
            config.audio.push({ label: device.label, value: device.deviceId });
          } else if (device.kind === DEVICE_KIND.VIDEO_INPUT) {
            config.video.push({ label: device.label, value: device.deviceId });
          }
          return config;
        },
        { audio: [] as Option[], video: [] as Option[] }
      );

      const defaultAudio =
        newConfig.audio.find(item => item.value === 'default') || noAudio;
      const defaultVideo = newConfig.video?.[0] || noVideo;

      setRecorderSettings({
        audioSource: [...newConfig.audio, noAudio],
        videoSource: [...newConfig.video, noVideo],
      });

      const saved = getUserSettings(defaultAudio, defaultVideo);

      setUserSettings(saved);
      setInitialUserSettingsState(saved);

      setRecordingError(null);
    } catch (error) {
      console.error('Error fetching devices:', error);
      updateErrorMessage();
      setRecorderSettings({
        audioSource: [noAudio],
        videoSource: [noVideo],
      });
    }
  };

  const onSubmitModalHandler = () => {
    setInitialUserSettingsState(userSettings);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(userSettings));
  };

  useEffect(() => {
    if (!ready) {
      return;
    }

    const handleDeviceChange = () => {
      fetchDevices();
    };

    if (navigator?.mediaDevices?.ondevicechange !== undefined) {
      navigator.mediaDevices.addEventListener(
        'devicechange',
        handleDeviceChange
      );

      fetchDevices();

      return () => {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          handleDeviceChange
        );
      };
    }

    // Fallback for unsupported browsers
    setRecorderSettings({
      audioSource: [noAudio],
      videoSource: [noVideo],
    });
  }, [ready, i18nFromHook.resolvedLanguage, noAudio, noVideo]);

  useEffect(() => {
    setRecorderSettings(prev => {
      const replaceOrAppend = (list: Option[], replacement: Option) => {
        const idx = list.findIndex(o => o.value === false);
        if (idx === -1) return [...list, replacement];
        const copy = [...list];
        copy[idx] = replacement;
        return copy;
      };

      return {
        audioSource: replaceOrAppend(prev.audioSource, noAudio),
        videoSource: replaceOrAppend(prev.videoSource, noVideo),
      };
    });
  }, [noAudio, noVideo]);

  return (
    <RecordSettingsContext.Provider
      value={{
        recorderSettings,
        userSettings,
        setUserSettings,
        onSubmitModalHandler,
        recordingError,
        updateErrorMessage,
        initialUserSettings: initialUserSettingsState,
      }}
    >
      {children}
    </RecordSettingsContext.Provider>
  );
};

export const useRecordSettings = () => {
  const context = useContext(RecordSettingsContext);
  if (context === undefined) {
    throw new Error(
      'useRecordSettings must be used within a RecordSettingsProvider'
    );
  }
  return context;
};
